import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { CompoApp } from '../../services/CompoApp';
import { ChecklistTemplateDetailAction } from './checklist-template-detail.action';
import { Observable } from 'rxjs/internal/Observable';
import { finalize, from, of, switchMap, throwError, timer } from 'rxjs';
import { combineLatestWith, delay, map, tap } from 'rxjs/operators';
import { ChecklistTemplateVersion } from '../../models/checklist-template/checklist-template-version';
import { WorkflowOverviewItem } from '../../models/workflow-overview/workflow-overview.model';
import {
  ChecklistTemplateEntryManagerXsState
} from '../checklist-template-entry-manager/checklist-template-entry-manager-xs.state';
import { ChecklistTemplateEntry } from '../../models/checklist-template-entry/checklist-template-entry.model';
import { ChecklistTemplate } from '../../models/checklist-template/checklist-template.model';
import {
  BoundingChecklistTemplateEntries
} from '../../models/checklist-template-entry/bounding-checklist-template-entries.model';
import { ChecklistTemplateType } from '../../models/checklist-template/checlist-template-type.model';
import { RightsScopeEnum } from '../../enums/rights/rights-scope.enum';
import { ChecklistTemplateStatus } from '../../enums/checklist-template/checklist-template-status.enum';
import { ChecklistEntryType } from '../../enums/checklist-entry/checklist-entry-type.enum';
import {
  ChecklistTemplateEntryManagerMode
} from '../../enums/checklist-template/checklist-template-entry-manager-mode.enum';
import { ResetStateDefaults } from '../base-action-class.model';
import {
  ChecklistTemplateEntryOption
} from '../../models/checklist-template-entry/checklist-template-entry-option.model';
import { ChecklistTemplateEntryRequestService } from '../../services/request/checklist-template-entry-request.service';
import {
  ChecklistTemplateEntryConvertToTableEntryAction
} from '../checklist-template-entry-table-row-conversion/checklist-template-entry-table-row-conversion.action';
import { ChecklistTemplateWorkflowOverviewBuilder } from '../../utils/checklist-template-workflow-overview-builder';
import {
  ChecklistTemplateOverallAssessmentAction
} from '../checklist-template-overall-assessment/checklist-template-overall-assessment.action';
import { ChecklistTemplateVersionXsState } from '../checklist-template-version/checklist-template-version-xs.state';
import { ChecklistFillOutAction } from '../checklist-fill-out/checklist-fill-out.action';
import { ChecklistTemplateDetailMenu } from '../../enums/checklist-template/checklist-template-detail-menu.enum';
import {
  ChecklistTemplateEntryDuplicationXsState
} from '../checklist-template-entry-duplication/checklist-template-entry-duplication-xs.state';
import {
  ChecklistTemplateEntryDuplicationAction
} from '../checklist-template-entry-duplication/checklist-template-entry-duplication.action';
import {
  ChecklistTemplateArchiveOptions
} from '../../models/checklist-template/checklist-tempalte-archive-options.model';
import {
  ChecklistTemplateIncompatibilityCount
} from '../../models/checklist-template/checklist-template-incompatibility-count.model';
import { DateUtils } from '../../utils/DateUtils';
import { environment } from '../../../environments/environment';

interface ChecklistTemplateDetailStateModel {
  id: string;
  checklistTemplate: ChecklistTemplate;
  checklistTemplateEntryIdsInOrder: string[];
  checklistTemplateEntryIdsInBlocks: string[][];
  checklistTemplateEntries: Map<string, ChecklistTemplateEntry>;
  versions: ChecklistTemplateVersion[];
  selectedVersion: ChecklistTemplateVersion;
  assessmentOptions: ChecklistTemplateEntryOption[];
  archiveOptions: ChecklistTemplateArchiveOptions;
  compatibilityInfo: ChecklistTemplateIncompatibilityCount;
  readOnly: boolean;
  inProgress: boolean;
  isCurrentVersionSelected: boolean;
  previewMode: boolean;
  unsavedChanges: boolean;
  selectedMenu: ChecklistTemplateDetailMenu;
}

const getDefaults = (): ChecklistTemplateDetailStateModel => ({
  id: null,
  checklistTemplate: null,
  checklistTemplateEntryIdsInOrder: null,
  checklistTemplateEntryIdsInBlocks: null,
  checklistTemplateEntries: null,
  versions: null,
  selectedVersion: null,
  assessmentOptions: null,
  archiveOptions: null,
  compatibilityInfo: null,
  readOnly: true,
  inProgress: false,
  isCurrentVersionSelected: false,
  previewMode: false,
  unsavedChanges: false,
  selectedMenu: ChecklistTemplateDetailMenu.CHECKLIST
});

@State<ChecklistTemplateDetailStateModel>({
  name: 'checklistTemplateDetail',
  defaults: getDefaults()
})
@Injectable()
export class ChecklistTemplateDetailXsState {

  //region CONSTANTS
  private static REQUEST_DELAY: number = 500;
  private static readonly SHARE_EXPIRATION_OFFSET_DAYS: number = 3;
  private static readonly SHARE_EXPIRATION_OFFSET_MS: number
    = ChecklistTemplateDetailXsState.SHARE_EXPIRATION_OFFSET_DAYS * 24 * 60 * 60 * 1000;
  //endregion

  //region INJECTIONS
  private app = inject(CompoApp);
  private checklistTemplateEntryRequestService = inject(ChecklistTemplateEntryRequestService);
  private store = inject(Store);

  //endregion

  //region SELECTORS
  @Selector()
  static id(state: ChecklistTemplateDetailStateModel): string | null {
    return state.id;
  }

  @Selector()
  static checklistTemplate(state: ChecklistTemplateDetailStateModel): ChecklistTemplate | null {
    return state.checklistTemplate;
  }

  @Selector()
  static checklistTemplateDeletable(state: ChecklistTemplateDetailStateModel): boolean {
    return state.checklistTemplate?.checklistCount == 0;
  }

  @Selector()
  static checklistTemplateType(state: ChecklistTemplateDetailStateModel): ChecklistTemplateType | null {
    return state.checklistTemplate?.type;
  }

  static displayedChecklistTemplateEntryIdsInBlocks() {
    return createSelector(
      [
        ChecklistTemplateDetailXsState,
        ChecklistTemplateEntryManagerXsState.active,
        ChecklistTemplateEntryManagerXsState.mode,
        ChecklistTemplateEntryManagerXsState.insertBeforeEntryId,
        ChecklistTemplateEntryManagerXsState.insertAfterEntryId,
        ChecklistTemplateEntryManagerXsState.groupedWithPrevious,
        ChecklistTemplateEntryManagerXsState.groupedWithNext
      ], (
        state: ChecklistTemplateDetailStateModel,
        entryManagerActive: boolean,
        entryManagerMode: ChecklistTemplateEntryManagerMode,
        insertBeforeEntryId: string,
        insertAfterEntryId: string,
        groupedWithPrevious: boolean | null,
        groupedWithNext: boolean | null
      ): (string | null)[][] | null => {

        if (!state.checklistTemplate || !state.checklistTemplateEntryIdsInBlocks) {
          return null;
        }

        if (entryManagerActive && entryManagerMode === ChecklistTemplateEntryManagerMode.CREATE) {

          if (!state.checklistTemplateEntryIdsInBlocks.length) {
            return [[null]];
          } else {

            if (!groupedWithPrevious && !groupedWithNext) {

              const checklistTemplateEntryIdsInBlocks: string[][] = [];

              for (const checklistTemplateEntryBlock of state.checklistTemplateEntryIdsInBlocks) {

                let checklistTemplateEntryIds: string[] = [];

                for (const checklistTemplateEntryId of checklistTemplateEntryBlock) {
                  if (checklistTemplateEntryId === insertAfterEntryId) {
                    checklistTemplateEntryIds.push(checklistTemplateEntryId);
                    checklistTemplateEntryIdsInBlocks.push(checklistTemplateEntryIds);
                    checklistTemplateEntryIdsInBlocks.push([null]);
                    checklistTemplateEntryIds = [];
                  } else if (checklistTemplateEntryId === insertBeforeEntryId) {
                    checklistTemplateEntryIdsInBlocks.push(checklistTemplateEntryIds);
                    checklistTemplateEntryIdsInBlocks.push([null]);
                    checklistTemplateEntryIds = [];
                    checklistTemplateEntryIds.push(checklistTemplateEntryId);
                  } else {
                    checklistTemplateEntryIds.push(checklistTemplateEntryId);
                  }
                }

                checklistTemplateEntryIdsInBlocks.push(checklistTemplateEntryIds);
              }

              return checklistTemplateEntryIdsInBlocks;
            } else {

              const checklistTemplateEntryIdsInBlocks: string[][] = [];

              for (const checklistTemplateEntryBlock of state.checklistTemplateEntryIdsInBlocks) {
                let currentBlock: string[] = [];

                for (const checklistTemplateEntryId of checklistTemplateEntryBlock) {

                  if (insertAfterEntryId === checklistTemplateEntryId) {
                    if (groupedWithPrevious) {
                      currentBlock.push(checklistTemplateEntryId);
                      currentBlock.push(null);
                    } else if (!groupedWithNext) {
                      currentBlock.push(checklistTemplateEntryId);
                      checklistTemplateEntryIdsInBlocks.push(currentBlock);
                      currentBlock = [null];
                    }
                  } else if (insertBeforeEntryId === checklistTemplateEntryId) {
                    if (!groupedWithPrevious) {
                      checklistTemplateEntryIdsInBlocks.push(currentBlock);
                      currentBlock = [null, checklistTemplateEntryId];
                    } else if (groupedWithNext) {
                      currentBlock.push(null);
                      currentBlock.push(checklistTemplateEntryId);
                    }
                  } else {
                    currentBlock.push(checklistTemplateEntryId);
                  }

                }

                checklistTemplateEntryIdsInBlocks.push(currentBlock);
              }

              return checklistTemplateEntryIdsInBlocks;
            }
          }
        } else {
          return state.checklistTemplateEntryIdsInBlocks || [];
        }
      });
  }

  static checklistTemplateEntry(id: string) {
    return createSelector(
      [ChecklistTemplateDetailXsState],
      (state: ChecklistTemplateDetailStateModel): ChecklistTemplateEntry | null => {
        return state.checklistTemplateEntries?.get(id) || null;
      });
  }

  static boundingChecklistTemplateEntries(id: string) {
    return createSelector(
      [
        ChecklistTemplateDetailXsState,
        ChecklistTemplateDetailXsState.displayedChecklistTemplateEntryIdsInBlocks(),
        ChecklistTemplateEntryManagerXsState.active,
        ChecklistTemplateEntryManagerXsState.mode,
        ChecklistTemplateEntryManagerXsState.checklistTemplateEntryId,
        ChecklistTemplateEntryManagerXsState.checklistTemplateEntry,
        ChecklistTemplateEntryManagerXsState.groupedWithNext
      ],
      (
        state: ChecklistTemplateDetailStateModel,
        displayedChecklistTemplateEntryIds: string[][],
        managerActive: boolean,
        managerMode: ChecklistTemplateEntryManagerMode,
        managedChecklistTemplateEntryId: string,
        managedChecklistTemplateEntry: ChecklistTemplateEntry,
        groupedWithNext: boolean
      ): BoundingChecklistTemplateEntries | null => {
        const row: number = displayedChecklistTemplateEntryIds?.findIndex(row => row.includes(id));

        if (row == -1) {
          return null;
        }

        const array1D = [].concat(...displayedChecklistTemplateEntryIds || []);

        const index = array1D.indexOf(id);

        if (index == -1) {
          return null;
        }

        if (index == -1) {
          return null;
        }

        const boundingChecklistTemplateEntries: BoundingChecklistTemplateEntries = {
          previous: null,
          next: null
        };

        if (index > 0) {
          const previousEntryId: string = array1D[index - 1];

          if (managerActive
            && managerMode === ChecklistTemplateEntryManagerMode.CREATE
            && managedChecklistTemplateEntryId === previousEntryId) {
            boundingChecklistTemplateEntries.previous = {
              id: managedChecklistTemplateEntryId,
              type: managedChecklistTemplateEntry?.type,
              groupedWithPrevious: managedChecklistTemplateEntry?.groupedWithPrevious
            };
          } else {
            const previousEntry: ChecklistTemplateEntry = state.checklistTemplateEntries.get(previousEntryId);
            boundingChecklistTemplateEntries.previous = {
              id: previousEntryId,
              type: previousEntry?.type,
              groupedWithPrevious: previousEntry?.groupedWithPrevious
            };
          }
        }

        if (index < array1D.length - 1) {
          const nextEntryId: string = array1D[index + 1];

          if (managerActive
            && managerMode === ChecklistTemplateEntryManagerMode.CREATE
            && managedChecklistTemplateEntryId === nextEntryId) {
            boundingChecklistTemplateEntries.next = {
              id: managedChecklistTemplateEntryId,
              type: managedChecklistTemplateEntry?.type,
              groupedWithPrevious: managedChecklistTemplateEntry?.groupedWithPrevious
            };
          } else {
            const nextEntry: ChecklistTemplateEntry = state.checklistTemplateEntries.get(nextEntryId);
            boundingChecklistTemplateEntries.next = {
              id: nextEntryId,
              type: nextEntry?.type,
              groupedWithPrevious: (
                managerActive
                && managerMode === ChecklistTemplateEntryManagerMode.CREATE
                && id === managedChecklistTemplateEntryId
              ) ? groupedWithNext : nextEntry?.groupedWithPrevious
            };
          }
        }

        return boundingChecklistTemplateEntries;
      });
  }

  static tableRowEntriesOfTable(tableEntryId: string) {
    return createSelector(
      [ChecklistTemplateDetailXsState],
      (state: ChecklistTemplateDetailStateModel): ChecklistTemplateEntry[] => {
        if (!state.checklistTemplateEntries) {
          return [];
        }

        return [...state.checklistTemplateEntries.values()].filter((entry) =>
          entry.type === ChecklistEntryType.TABLE_ROW && entry.parentChecklistTemplateEntry?.id === tableEntryId
        );
      });
  }

  @Selector()
  static checklistTemplateEntries(state: ChecklistTemplateDetailStateModel): Map<string, ChecklistTemplateEntry> {
    return state.checklistTemplateEntries;
  }

  @Selector()
  static checklistTemplateEntryIdsInBlocks(state: ChecklistTemplateDetailStateModel): string[][] {
    return state.checklistTemplateEntryIdsInBlocks;
  }

  @Selector()
  static checklistTemplateEntryIdsInOrder(state: ChecklistTemplateDetailStateModel): string[] {
    return state.checklistTemplateEntryIdsInOrder;
  }

  static moveEntryBackEnabled(id: string) {
    return createSelector(
      [ChecklistTemplateDetailXsState],
      (state: ChecklistTemplateDetailStateModel): boolean => {
        const block: string[] = state?.checklistTemplateEntryIdsInBlocks?.find(row => row.includes(id));

        if (block === undefined) {
          return false;
        }

        const indexOfEntry: number = block.indexOf(id);

        if (indexOfEntry === 0) {
          return false;
        }

        const entry: ChecklistTemplateEntry = state.checklistTemplateEntries.get(id);

        if (entry.type === ChecklistEntryType.TABLE_ROW) {
          const previousEntryId: string = block[indexOfEntry - 1];
          const previousEntry: ChecklistTemplateEntry = state.checklistTemplateEntries.get(previousEntryId);
          return previousEntry.type === ChecklistEntryType.TABLE_ROW;
        } else {
          return true;
        }
      });
  }

  static moveEntryForwardEnabled(id: string) {
    return createSelector(
      [ChecklistTemplateDetailXsState],
      (state: ChecklistTemplateDetailStateModel): boolean => {
        const block: string[] = state?.checklistTemplateEntryIdsInBlocks?.find(row => row.includes(id));

        if (block === undefined) {
          return false;
        }

        const indexOfEntry: number = block.indexOf(id);

        if (indexOfEntry === block.length - 1) {
          return false;
        }

        const entry: ChecklistTemplateEntry = state.checklistTemplateEntries.get(id);

        if (entry.type === ChecklistEntryType.TABLE) {
          for (let i = indexOfEntry + 1; i < block.length; i++) {
            const entryType: ChecklistEntryType = state.checklistTemplateEntries.get(block[i])?.type;
            if (entryType && entryType !== ChecklistEntryType.TABLE_ROW) {
              return true;
            }
          }
          return false;
        }

        if (entry.type === ChecklistEntryType.TABLE_ROW) {
          const nextEntryId: string = block[indexOfEntry + 1];
          return state.checklistTemplateEntries.get(nextEntryId).type === ChecklistEntryType.TABLE_ROW;
        }

        return true;
      });
  }

  @Selector()
  static checklistTemplateWorkflowOverview(state: ChecklistTemplateDetailStateModel): WorkflowOverviewItem[] | null {

    if (!state.checklistTemplateEntryIdsInOrder || !state.checklistTemplateEntries) {
      return null;
    }

    return ChecklistTemplateWorkflowOverviewBuilder.buildTreeForWorkflowOverview(
      state.checklistTemplateEntryIdsInOrder,
      state.checklistTemplateEntries,
      null,
      null,
      true,
      true
    );
  }

  @Selector()
  static versions(state: ChecklistTemplateDetailStateModel): ChecklistTemplateVersion[] {
    return state.versions;
  }

  @Selector()
  static selectedVersion(state: ChecklistTemplateDetailStateModel): ChecklistTemplateVersion {
    return state.selectedVersion;
  }

  @Selector()
  static unsavedChanges(state: ChecklistTemplateDetailStateModel): boolean {
    return state.unsavedChanges;
  }

  @Selector()
  static selectedMenu(state: ChecklistTemplateDetailStateModel): ChecklistTemplateDetailMenu {
    return state.selectedMenu;
  }

  @Selector()
  static currentVersion(state: ChecklistTemplateDetailStateModel): ChecklistTemplateVersion {
    return state.checklistTemplate?.currentVersion;
  }

  @Selector()
  static hasReferences(state: ChecklistTemplateDetailStateModel): boolean {
    return true;
  }

  @Selector()
  static assessmentOptions(state: ChecklistTemplateDetailStateModel): ChecklistTemplateEntryOption[] {
    return state.assessmentOptions;
  }

  @Selector()
  static archiveOptions(state: ChecklistTemplateDetailStateModel): ChecklistTemplateArchiveOptions {
    return state.archiveOptions;
  }

  @Selector()
  static compatibilityInfo(state: ChecklistTemplateDetailStateModel): ChecklistTemplateIncompatibilityCount {
    return state.compatibilityInfo;
  }

  @Selector()
  static readOnly(state: ChecklistTemplateDetailStateModel): boolean {
    return state.readOnly;
  }

  @Selector()
  static isCurrentVersionSelected(state: ChecklistTemplateDetailStateModel): boolean {
    return state.isCurrentVersionSelected;
  }

  @Selector()
  static inProgress(state: ChecklistTemplateDetailStateModel): boolean {
    return state.inProgress;
  }

  @Selector()
  static previewMode(state: ChecklistTemplateDetailStateModel): boolean {
    return state.previewMode;
  }

  //endregion

  //region ACTIONS
  @Action(ChecklistTemplateDetailAction.Fetch)
  public fetch(
    ctx: StateContext<ChecklistTemplateDetailStateModel>,
    action: ChecklistTemplateDetailAction.Fetch
  ): Observable<void> {
    const state: ChecklistTemplateDetailStateModel = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Checklist template request is in progress!'));
    }

    if (!action.id) {
      return throwError(() => new Error('Checklist template id cannot be null!'));
    }

    this.store.dispatch(new ChecklistTemplateOverallAssessmentAction.ResetState());

    ctx.patchState({
      ...getDefaults(),
      selectedMenu: state.selectedMenu,
      inProgress: true
    });

    return from(this.app.checklistTemplateService.getById(action.id))
      .pipe(
        combineLatestWith(
          this.app.checklistTemplateService.getAllVersions(action.id),
          this.checklistTemplateEntryRequestService.getAssessmentOptions(),
          timer(ChecklistTemplateDetailXsState.REQUEST_DELAY)
        ),
        map(([checklistTemplate, checklistTemplateVersions, assessmentOptions, _]
             : [ChecklistTemplate, ChecklistTemplateVersion[], ChecklistTemplateEntryOption[], any]): void => {
          const parsedChecklistTemplate: ChecklistTemplate = checklistTemplate ? {
            ...checklistTemplate,
            currentVersion: ChecklistTemplateDetailXsState.parseVersionDates(checklistTemplate.currentVersion)
          } : checklistTemplate;
          const parsedVersions: ChecklistTemplateVersion[] = checklistTemplateVersions?.map(ChecklistTemplateDetailXsState.parseVersionDates);

          ctx.patchState({
            id: checklistTemplate.id,
            checklistTemplate: parsedChecklistTemplate,
            versions: parsedVersions,
            assessmentOptions: assessmentOptions,
            isCurrentVersionSelected: true
          });
          this.setChecklistTemplateEntries(ctx, checklistTemplate?.checklistTemplateEntries);
          this.setReadOnly(ctx);
          this.store.dispatch(new ChecklistTemplateOverallAssessmentAction.Fetch(checklistTemplate.currentVersion.id));
        }),
        finalize(() => ctx.patchState({ inProgress: false }))
      );
  }

  @Action(ChecklistTemplateDetailAction.FetchVersions)
  public fetchVersions(
    ctx: StateContext<ChecklistTemplateDetailStateModel>,
    action: ChecklistTemplateDetailAction.FetchVersions
  ): Observable<void> {
    const state: ChecklistTemplateDetailStateModel = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Checklist template request is in progress!'));
    }
    if (!state.id) {
      return throwError(() => new Error('Cannot fetch version if checklist template id is not set!'));
    }

    if (!action.force && state.versions?.length > 0) {
      return of();
    }

    ctx.patchState({
      inProgress: true
    });

    return from(this.app.checklistTemplateService.getAllVersions(state.id))
      .pipe(
        map(([checklistTemplateVersions, _]: [ChecklistTemplateVersion[], any]): void => {
          const parsedVersions: ChecklistTemplateVersion[] = checklistTemplateVersions?.map(ChecklistTemplateDetailXsState.parseVersionDates);
          const clipboardVersionId: string
            = this.store.selectSnapshot(ChecklistTemplateEntryDuplicationXsState.duplicateAllEntries)?.checklistTemplateVersionId;

          if (clipboardVersionId) {
            const clipboardVersion: ChecklistTemplateVersion
              = parsedVersions?.find((version: ChecklistTemplateVersion) => version.id === clipboardVersionId);
            if (!!clipboardVersion && clipboardVersion.status !== ChecklistTemplateStatus.IN_PROGRESS) {
              this.store.dispatch(new ChecklistTemplateEntryDuplicationAction.ResetState());
            }
          }

          ctx.patchState({
            versions: parsedVersions
          });
        }),
        finalize(() => ctx.patchState({ inProgress: false }))
      );
  }

  @Action(ChecklistTemplateDetailAction.CopyCompleteChecklist)
  public copyCompleteChecklist(
    ctx: StateContext<ChecklistTemplateDetailStateModel>,
    action: ChecklistTemplateDetailAction.CopyCompleteChecklist
  ) {
    if (!action.checklistTemplateId) {
      return throwError(() => new Error('Checklist template version id is not set!'));
    }

    ctx.patchState({
      ...getDefaults(),
      inProgress: true
    });
  }

  @Action(ChecklistTemplateDetailAction.SelectVersion)
  public selectVersion(
    ctx: StateContext<ChecklistTemplateDetailStateModel>,
    action: ChecklistTemplateDetailAction.SelectVersion
  ): Observable<void> {
    const state: ChecklistTemplateDetailStateModel = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Checklist template request is in progress!'));
    }
    if (!state.id) {
      return throwError(() => new Error('Cannot fetch version if checklist template id is not set!'));
    }
    if (!action.version) {
      return throwError(() => new Error('Version cannot be null!'));
    }

    let selectedVersion: ChecklistTemplateVersion;

    for (const version of state.versions) {
      if (version.version === action.version) {
        selectedVersion = version;
        break;
      }
    }

    if (selectedVersion === null) {
      return throwError(() => new Error('Cannot find version: ' + action.version));
    }

    this.store.dispatch(new ChecklistTemplateOverallAssessmentAction.ResetState());

    ctx.patchState({
      inProgress: true,
      selectedVersion,
      checklistTemplateEntryIdsInOrder: null,
      checklistTemplateEntryIdsInBlocks: null,
      checklistTemplateEntries: null
    });

    return from(this.app.checklistTemplateService.getByIdAndVersion(state.id, action.version))
      .pipe(
        combineLatestWith(timer(ChecklistTemplateDetailXsState.REQUEST_DELAY)),
        map(([checklistTemplate, _]: [ChecklistTemplate, any]): void => {
          const parsedChecklistTemplate: ChecklistTemplate = checklistTemplate ? {
            ...checklistTemplate,
            currentVersion: ChecklistTemplateDetailXsState.parseVersionDates(checklistTemplate.currentVersion)
          } : checklistTemplate;

          ctx.patchState({
            checklistTemplate: parsedChecklistTemplate,
            isCurrentVersionSelected: false
          });
          this.setChecklistTemplateEntries(ctx, checklistTemplate?.checklistTemplateEntries);
          this.setReadOnly(ctx);
          this.store.dispatch(new ChecklistTemplateOverallAssessmentAction.Fetch(checklistTemplate.currentVersion.id));
        }),
        finalize(() => ctx.patchState({ inProgress: false }))
      );
  }

  @Action(ChecklistTemplateDetailAction.SelectCurrentVersion)
  public selectCurrentVersion(ctx: StateContext<ChecklistTemplateDetailStateModel>): Observable<void> {
    const state: ChecklistTemplateDetailStateModel = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Checklist template request is in progress!'));
    }

    if (!state.id) {
      return throwError(() => new Error('Cannot fetch version if checklist template id is not set!'));
    }

    this.store.dispatch(new ChecklistTemplateOverallAssessmentAction.ResetState());

    ctx.patchState({
      inProgress: true,
      selectedVersion: null,
      checklistTemplateEntryIdsInOrder: null,
      checklistTemplateEntryIdsInBlocks: null,
      checklistTemplateEntries: null
    });

    if (state.previewMode) {
      this.store.dispatch(new ChecklistFillOutAction.ResetState());
    }

    return from(this.app.checklistTemplateService.getById(state.id))
      .pipe(
        combineLatestWith(timer(ChecklistTemplateDetailXsState.REQUEST_DELAY)),
        switchMap(([checklistTemplate, _]: [ChecklistTemplate, any]) => {
          if (state.previewMode) {
            return this.store.dispatch(
              new ChecklistFillOutAction.InitPreview(state.id, checklistTemplate.currentVersion?.id, null, null, null)
            ).pipe(map(() => checklistTemplate));
          } else {
            return of(checklistTemplate);
          }
        }),
        map((checklistTemplate: ChecklistTemplate): void => {
          ctx.patchState({
            checklistTemplate: checklistTemplate,
            isCurrentVersionSelected: true
          });
          this.setChecklistTemplateEntries(ctx, checklistTemplate?.checklistTemplateEntries);
          this.setReadOnly(ctx);
          this.store.dispatch(new ChecklistTemplateOverallAssessmentAction.Fetch(checklistTemplate.currentVersion.id));
        }),
        finalize(() => ctx.patchState({ inProgress: false }))
      );
  }

  @Action(ChecklistTemplateDetailAction.SetAllowJobReferencesFlag)
  public setAllowJobReferencesFlag(
    ctx: StateContext<ChecklistTemplateDetailStateModel>,
    action: ChecklistTemplateDetailAction.SetAllowJobReferencesFlag
  ): Observable<void> {
    const state: ChecklistTemplateDetailStateModel = ctx.getState();

    if (state.readOnly) {
      return throwError(() => new Error('Cannot delete read only checklist template!'));
    }

    if (!state.id) {
      return throwError(() => new Error('Cannot update entry if checklist template id is not set!'));
    }

    if (state.inProgress
      || this.store.selectSnapshot(ChecklistTemplateVersionXsState.anyActionInProgress(state.id))) {
      return throwError(() => new Error('Checklist template request is in progress!'));
    }

    if (state.checklistTemplate?.currentVersion?.allowJobReferences === action.allowJobReferences) {
      return of();
    }

    return this.app.checklistTemplateService.setAllowJobReferencesFlag(state.id, action.allowJobReferences)
      .pipe(
        tap(() => {
          const updatedVersion: ChecklistTemplateVersion = {
            ...state.checklistTemplate.currentVersion,
            allowJobReferences: action.allowJobReferences
          };
          ctx.patchState({
            checklistTemplate: {
              ...state.checklistTemplate,
              currentVersion: updatedVersion
            }
          });
        })
      );
  }

  @Action(ChecklistTemplateDetailAction.SetUseOrganizationLogoFlag)
  public setUseOrganizationLogoFlag(
    ctx: StateContext<ChecklistTemplateDetailStateModel>,
    action: ChecklistTemplateDetailAction.SetUseOrganizationLogoFlag
  ): Observable<void> {
    const state: ChecklistTemplateDetailStateModel = ctx.getState();

    if (state.readOnly) {
      return throwError(() => new Error('Cannot delete read only checklist template!'));
    }

    if (!state.id) {
      return throwError(() => new Error('Cannot update entry if checklist template id is not set!'));
    }

    if (state.inProgress
      || this.store.selectSnapshot(ChecklistTemplateVersionXsState.anyActionInProgress(state.id))) {
      return throwError(() => new Error('Checklist template request is in progress!'));
    }

    if (state.checklistTemplate?.currentVersion?.useOrganizationLogo === action.useOrganizationLogo) {
      return of();
    }

    return this.app.checklistTemplateService.setUseOrganizationLogoFlag(state.id, action.useOrganizationLogo)
      .pipe(
        tap(() => {
          const updatedVersion: ChecklistTemplateVersion = {
            ...state.checklistTemplate.currentVersion,
            useOrganizationLogo: action.useOrganizationLogo
          };
          ctx.patchState({
            checklistTemplate: {
              ...state.checklistTemplate,
              currentVersion: updatedVersion
            }
          });
        })
      );
  }

  @Action(ChecklistTemplateDetailAction.SetAssessmentIsRequiredFlag)
  public setAssessmentIsRequiredFlag(
    ctx: StateContext<ChecklistTemplateDetailStateModel>,
    action: ChecklistTemplateDetailAction.SetAssessmentIsRequiredFlag
  ): Observable<void> {
    const state: ChecklistTemplateDetailStateModel = ctx.getState();

    if (state.readOnly) {
      return throwError(() => new Error('Cannot delete read only checklist template!'));
    }

    if (!state.id) {
      return throwError(() => new Error('Cannot update entry if checklist template id is not set!'));
    }

    if (state.inProgress
      || this.store.selectSnapshot(ChecklistTemplateVersionXsState.anyActionInProgress(state.id))) {
      return throwError(() => new Error('Checklist template request is in progress!'));
    }

    if (state.checklistTemplate?.currentVersion?.assessmentIsRequired === action.assessmentIsRequired) {
      return of();
    }

    return this.app.checklistTemplateService.setAssessmentIsRequiredFlag(state.id, action.assessmentIsRequired)
      .pipe(
        tap(() => {
          const updatedVersion: ChecklistTemplateVersion = {
            ...state.checklistTemplate.currentVersion,
            assessmentIsRequired: action.assessmentIsRequired
          };
          ctx.patchState({
            checklistTemplate: {
              ...state.checklistTemplate,
              currentVersion: updatedVersion
            }
          });
        })
      );
  }

  @Action(ChecklistTemplateDetailAction.SetSharedFlag)
  public setSharedFlag(
    ctx: StateContext<ChecklistTemplateDetailStateModel>,
    action: ChecklistTemplateDetailAction.SetSharedFlag
  ): Observable<void> {
    const state: ChecklistTemplateDetailStateModel = ctx.getState();

    const versionId: string = state.selectedVersion?.id || state.checklistTemplate?.currentVersion?.id;
    if (!versionId) {
      return throwError(() => new Error('Checklist template version id cannot be null!'));
    }

    if (state.inProgress
      || this.store.selectSnapshot(ChecklistTemplateVersionXsState.anyActionInProgress(state.id))) {
      return throwError(() => new Error('Checklist template request is in progress!'));
    }

    return this.app.checklistTemplateService.setSharedFlag(versionId, action.shared)
      .pipe(
        map((expiration: Date) => {
          const patch: Partial<ChecklistTemplateDetailStateModel> = {};

          if (state.checklistTemplate?.currentVersion?.id === versionId) {
            patch.checklistTemplate = {
              ...state.checklistTemplate,
              currentVersion: {
                ...state.checklistTemplate.currentVersion,
                shared: action.shared,
                shareExpiration: expiration
              }
            };
          }

          if (state.selectedVersion?.id === versionId) {
            patch.selectedVersion = {
              ...state.selectedVersion,
              shared: action.shared,
              shareExpiration: expiration
            };
          }

          if (state.versions) {
            patch.versions = state.versions.map(v => v.id === versionId ? {
              ...v,
              shared: action.shared,
              shareExpiration: expiration
            } : v);
          }

          ctx.patchState(patch);
        })
      );
  }

  @Action(ChecklistTemplateDetailAction.SetShareExpiration)
  public setShareExpiration(
    ctx: StateContext<ChecklistTemplateDetailStateModel>,
    action: ChecklistTemplateDetailAction.SetShareExpiration
  ): Observable<void> {
    const state: ChecklistTemplateDetailStateModel = ctx.getState();

    const versionId: string = state.selectedVersion?.id || state.checklistTemplate?.currentVersion?.id;
    if (!versionId) {
      return throwError(() => new Error('Checklist template version id cannot be null!'));
    }

    if (state.inProgress
      || this.store.selectSnapshot(ChecklistTemplateVersionXsState.anyActionInProgress(state.id))) {
      return throwError(() => new Error('Checklist template request is in progress!'));
    }

    let newExpiration: Date | null = null;
    if (action.shareExpiration) {
      newExpiration = new Date(action.shareExpiration);
      DateUtils.resetClockTime(newExpiration);
    }

    const patch: Partial<ChecklistTemplateDetailStateModel> = {};

    if (state.checklistTemplate?.currentVersion?.id === versionId) {
      patch.checklistTemplate = {
        ...state.checklistTemplate,
        currentVersion: {
          ...state.checklistTemplate.currentVersion,
          shareExpiration: newExpiration
        }
      };
    }

    if (state.selectedVersion?.id === versionId) {
      patch.selectedVersion = {
        ...state.selectedVersion,
        shareExpiration: newExpiration
      };
    }

    if (state.versions) {
      patch.versions = state.versions.map(v => v.id === versionId ? {
        ...v,
        shareExpiration: newExpiration
      } : v);
    }

    ctx.patchState(patch);

    return this.app.checklistTemplateService.setShareExpiration(versionId, action.shareExpiration);
  }

  @Action(ChecklistTemplateDetailAction.Delete)
  public delete(ctx: StateContext<ChecklistTemplateDetailStateModel>): Observable<void> {
    const state: ChecklistTemplateDetailStateModel = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Checklist template request is in progress!'));
    }

    if (!state.id) {
      return throwError(() => new Error('Cannot update entry if checklist template id is not set!'));
    }

    // DLOUHY-12876 S-Admin Sync-Module: This is a temporary solution. Deletion must be validated across all instances, which will be implemented later.
    const scope: RightsScopeEnum = ChecklistTemplateType.getRightScope(state.checklistTemplate?.type?.id);
    if (environment.objackSpEnabled && (scope === RightsScopeEnum.GLOBAL || scope === RightsScopeEnum.SERVICE_PROVIDER)) {
      return throwError(() => new Error('Deletion of global and service provider checklist templates is prohibited!'));
    }

    return this.app.checklistTemplateService.deleteChecklistTemplatePermanent(state.id);
  }

  @Action([
    ChecklistTemplateDetailAction.ChecklistTemplateEntryCreated,
    ChecklistTemplateDetailAction.ChecklistTemplateEntryUpdated,
    ChecklistTemplateDetailAction.ChecklistTemplateEntryDeleted,
    ChecklistTemplateDetailAction.ChecklistTemplateEntryGrouped,
    ChecklistTemplateDetailAction.ChecklistTemplateEntryUngrouped,
    ChecklistTemplateDetailAction.ChecklistTemplateEntryReordered,
    ChecklistTemplateDetailAction.ChecklistTemplateEntryConditionChanged,
    ChecklistTemplateDetailAction.ChecklistTemplateEntryDuplicated,
    ChecklistTemplateEntryConvertToTableEntryAction.EntryReFetchNeeded,
    ChecklistTemplateDetailAction.ChecklistTemplateEntryShifted
  ])
  public getChecklistTemplateEntries(
    ctx: StateContext<ChecklistTemplateDetailStateModel>
  ): Observable<void> {
    const state: ChecklistTemplateDetailStateModel = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Checklist template request is in progress!'));
    }

    if (!state.id) {
      return throwError(() => new Error('Cannot set entry if checklist template id is not set!'));
    }

    return from(this.checklistTemplateEntryRequestService.getChecklistTemplateEntries(state.id))
      .pipe(map((checklistTemplateEntries: ChecklistTemplateEntry[]) => {
        if (checklistTemplateEntries.length === 0) {
          const clipboardVersionId: string
            = this.store.selectSnapshot(ChecklistTemplateEntryDuplicationXsState.duplicateAllEntries)?.checklistTemplateVersionId;

          if (state?.checklistTemplate?.currentVersion?.id === clipboardVersionId) {
            this.store.dispatch(new ChecklistTemplateEntryDuplicationAction.ResetState());
          }
        } else {
          const clipboardEntryId: string
            = this.store.selectSnapshot(ChecklistTemplateEntryDuplicationXsState.duplicateEntries)?.entryId;

          if (clipboardEntryId
            && !checklistTemplateEntries.some((entry: ChecklistTemplateEntry) => entry.id === clipboardEntryId)
          ) {
            this.store.dispatch(new ChecklistTemplateEntryDuplicationAction.ResetState());
          }
        }

        this.setChecklistTemplateEntries(ctx, checklistTemplateEntries);
      }));
  }

  @Action(ChecklistTemplateDetailAction.ChecklistTemplateEntryBlockDragged)
  public entryBlockDragged(
    ctx: StateContext<ChecklistTemplateDetailStateModel>,
    action: ChecklistTemplateDetailAction.ChecklistTemplateEntryBlockDragged
  ): void {
    const state: ChecklistTemplateDetailStateModel = ctx.getState();

    if (state.inProgress) {
      throw new Error('Checklist template request is in progress!');
    }

    if (!state.id) {
      throw new Error('Cannot set entry if checklist template id is not set!');
    }

    if (action.oldIndex == null || action.newIndex == null) {
      throw new Error('Both old index and new index needs to be set!');
    }

    const blocks: string[][] = [...state.checklistTemplateEntryIdsInBlocks];
    const draggedBlock: string[] = blocks[action.oldIndex];
    blocks.splice(action.oldIndex, 1);
    blocks.splice(action.newIndex, 0, draggedBlock);

    ctx.patchState({
      checklistTemplateEntryIdsInBlocks: blocks
    });
  }

  @Action(ChecklistTemplateDetailAction.EnterPreviewMode)
  public enterPreviewMode(
    ctx: StateContext<ChecklistTemplateDetailStateModel>
  ): void {
    const state: ChecklistTemplateDetailStateModel = ctx.getState();

    if (state.inProgress) {
      throw new Error('Checklist template request is in progress!');
    }

    if (!state.id) {
      throw new Error('Cannot enter preview mode if checklist template id is not set!');
    }

    if (state.previewMode) {
      return;
    }

    ctx.patchState({ previewMode: true });

    this.store.dispatch(
      new ChecklistFillOutAction.InitPreview(state.id, state.selectedVersion
        ? state.selectedVersion?.id :
        state.checklistTemplate.currentVersion?.id, null, null, null)
    );
  }

  @Action(ChecklistTemplateDetailAction.ExitPreviewMode)
  public exitPreviewMode(
    ctx: StateContext<ChecklistTemplateDetailStateModel>
  ): void {
    const state: ChecklistTemplateDetailStateModel = ctx.getState();

    if (state.inProgress) {
      throw new Error('Checklist template request is in progress!');
    }

    if (!state.previewMode) {
      return;
    }

    ctx.patchState({ previewMode: false });

    this.store.dispatch(new ChecklistFillOutAction.ResetState());
  }

  @Action([
    ChecklistTemplateDetailAction.ResetState,
    ResetStateDefaults
  ])
  public resetState(ctx: StateContext<ChecklistTemplateDetailStateModel>): void {
    ctx.setState(getDefaults());
    this.store.dispatch(new ChecklistTemplateOverallAssessmentAction.ResetState());
  }

  @Action(ChecklistTemplateDetailAction.SetUnsavedChanged)
  public setUnsavedChanged(ctx: StateContext<ChecklistTemplateDetailStateModel>, action: ChecklistTemplateDetailAction.SetUnsavedChanged): void {
    ctx.patchState({
      unsavedChanges: action?.value
    });
  }

  @Action(ChecklistTemplateDetailAction.SetSelectedMenu)
  public setSelectedMenu(ctx: StateContext<ChecklistTemplateDetailStateModel>, action: ChecklistTemplateDetailAction.SetSelectedMenu): void {
    ctx.patchState({
      selectedMenu: action?.menu
    });
  }

  @Action(ChecklistTemplateDetailAction.GetArchiveOptions)
  public getArchiveOptions(
    ctx: StateContext<ChecklistTemplateDetailStateModel>
  ): Observable<void> {
    const state: ChecklistTemplateDetailStateModel = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Checklist template request is in progress!'));
    }

    if (!state.id) {
      return throwError(() => new Error('Cannot fetch version if checklist template id is not set!'));
    }

    ctx.patchState({ inProgress: true });

    return this.app.checklistTemplateService.getChecklistTemplateArchiveOptions(state.id)
      .pipe(
        delay(ChecklistTemplateDetailXsState.REQUEST_DELAY),
        map((archiveOptions: ChecklistTemplateArchiveOptions) => {
          ctx.patchState({ archiveOptions });
        }),
        finalize(() => ctx.patchState({ inProgress: false }))
      );
  }

  @Action(ChecklistTemplateDetailAction.GetCompatibilityInfo)
  public getCompatibilityInfo(
    ctx: StateContext<ChecklistTemplateDetailStateModel>
  ): Observable<void> {
    const state: ChecklistTemplateDetailStateModel = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Checklist template request is in progress!'));
    }

    if (!state.id) {
      return throwError(() => new Error('Cannot fetch version if checklist template id is not set!'));
    }

    ctx.patchState({ inProgress: true });

    return this.app.checklistTemplateService.getCompatibilityInfo(state.id)
      .pipe(
        delay(ChecklistTemplateDetailXsState.REQUEST_DELAY),
        map((compatibilityInfo: ChecklistTemplateIncompatibilityCount) => {
          ctx.patchState({ compatibilityInfo });
        }),
        finalize(() => ctx.patchState({ inProgress: false }))
      );
  }

  //endregion

  //region METHODS
  private setChecklistTemplateEntries(
    ctx: StateContext<ChecklistTemplateDetailStateModel>,
    checklistTemplateEntries: ChecklistTemplateEntry[]
  ): void {
    if (!checklistTemplateEntries) {
      return;
    }

    if (checklistTemplateEntries.length == 0) {
      ctx.patchState({
        checklistTemplateEntryIdsInOrder: [],
        checklistTemplateEntryIdsInBlocks: [],
        checklistTemplateEntries: new Map()
      });

      return;
    }

    const orderedChecklistTemplateEntries: ChecklistTemplateEntry[] = [...checklistTemplateEntries]
      .sort((a: ChecklistTemplateEntry, b: ChecklistTemplateEntry) => a.column - b.column);

    const checklistTemplateEntryIdsInOrder: string[] = [];
    const checklistTemplateEntryIdsInBlocks: string[][] = [];
    const checklistTemplateEntryMap: Map<string, ChecklistTemplateEntry>
      = new Map<string, ChecklistTemplateEntry>();

    let first: boolean = true;
    let currentGroup: string[] = [];

    for (const checklistTemplateEntry of orderedChecklistTemplateEntries) {
      if (!checklistTemplateEntryMap.has(checklistTemplateEntry.id)) {
        checklistTemplateEntryIdsInOrder.push(checklistTemplateEntry.id);
        checklistTemplateEntryMap.set(checklistTemplateEntry.id, checklistTemplateEntry);

        if (!checklistTemplateEntry.groupedWithPrevious && !first) {
          checklistTemplateEntryIdsInBlocks.push(currentGroup);
          currentGroup = [];
        }

        currentGroup.push(checklistTemplateEntry.id);
      }

      if (first) {
        first = false;
      }
    }

    checklistTemplateEntryIdsInBlocks.push(currentGroup);

    ctx.patchState({
      checklistTemplateEntryIdsInOrder,
      checklistTemplateEntryIdsInBlocks,
      checklistTemplateEntries: checklistTemplateEntryMap
    });
  }

  private setReadOnly(
    ctx: StateContext<ChecklistTemplateDetailStateModel>
  ): void {
    const state: ChecklistTemplateDetailStateModel = ctx.getState();

    if (!state.checklistTemplate) {
      return;
    }

    let readOnly: boolean;

    if (state.selectedVersion) {
      readOnly = state.selectedVersion.status !== ChecklistTemplateStatus.IN_PROGRESS;
    } else {
      readOnly = state.checklistTemplate.currentVersion.status !== ChecklistTemplateStatus.IN_PROGRESS;
    }

    ctx.patchState({
      readOnly
    });
  }

  private static parseVersionDates(version: ChecklistTemplateVersion): ChecklistTemplateVersion {
    if (!version) {
      return version;
    }
    const parsed: ChecklistTemplateVersion = { ...version };
    if (version.shareExpiration) {
      parsed.shareExpiration = new Date(version.shareExpiration);
      DateUtils.resetClockTime(parsed.shareExpiration);
    }
    return parsed;
  }

  //endregion
}
